import { FC, useEffect, useMemo, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Box, Button, Chip, IconButton, Tooltip, Typography, Drawer } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import CreatedByCell from "./CreatedByCell";
import LocationSelect from "./pickers/LocationSelect";
import { FilterBar, FilterSearch, FilterSelect } from "./InvFilters";
import Filters from "../Filters/Filters";
import AuditTimeline from "../AuditTimeline/AuditTimeline";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchGRNs } from "../../store/grnStore/grnThunk";
import { ApiStatus, defaultRowsPerPageOptions } from "../../utils/api.types";

const STATUS_COLOR: Record<string, string> = { draft: "#b08900", posted: "#49a563", cancelled: "#c33b3b" };
const GRN_STATUSES = ["draft", "posted", "cancelled"];

const GRNList: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { grns, status, rowCount } = useAppSelector((state) => state.grns);

  // Server-side filters (resolved on the backend via query params).
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [locationId, setLocationId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Posted-on range (from the shared Filters date-range dropdown).
  const [postedFrom, setPostedFrom] = useState("");
  const [postedTo, setPostedTo] = useState("");
  // Bumped on reset to remount the date-range dropdown back to "Select Date".
  const [filtersKey, setFiltersKey] = useState(0);

  // Server-side pagination (0-based page); default 25 rows/page.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditGRN, setAuditGRN] = useState<any>(null);

  const handleOpenAudit = (row: any) => {
    setAuditGRN(row);
    setAuditDialogOpen(true);
  };

  const handleCloseAudit = () => {
    setAuditDialogOpen(false);
    setAuditGRN(null);
  };

  const buildParams = () => {
    const p: string[] = [];
    if (debouncedSearch) p.push(`search=${encodeURIComponent(debouncedSearch)}`);
    if (locationId) p.push(`locationId=${locationId}`);
    if (statusFilter) p.push(`status=${statusFilter}`);
    if (postedFrom) p.push(`postedFrom=${postedFrom}`);
    if (postedTo) p.push(`postedTo=${postedTo}`);
    p.push(`page=${page}`);
    p.push(`limit=${pageSize}`);
    return p.join("&");
  };

  const hasFilters = !!(debouncedSearch || locationId || statusFilter || postedFrom || postedTo);
  const resetFilters = () => {
    setSearch("");
    setLocationId("");
    setStatusFilter("");
    setPostedFrom("");
    setPostedTo("");
    // Remount the date-range dropdown so it reinitialises back to "Select Date".
    setFiltersKey((k) => k + 1);
    setPage(0);
  };
  // Filter changes reset to the first page.
  const onLocation = (v: string) => { setLocationId(v); setPage(0); };
  const onStatus = (v: string) => { setStatusFilter(v); setPage(0); };
  // Posted-on range comes from the shared Filters dropdown. It emits a query string
  // like "start=YYYY-MM-DD&end=YYYY-MM-DD&voucherType=all"; read ONLY start/end and
  // map to postedFrom/postedTo. Filters calls this from a setTimeout, so batch the
  // updates to avoid a partial (postedFrom-only) first fetch.
  const handlePostedDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    const from = params.get("start") || "";
    const to = params.get("end") || "";
    unstable_batchedUpdates(() => {
      setPostedFrom(from);
      setPostedTo(to);
      setPage(0);
    });
  };
  const onPageSizeChange = (n: number) => { setPageSize(n); setPage(0); };

  // Debounce only the free-text search (resets to page 0 once it settles).
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Load when filters or pagination change.
  useEffect(() => {
    dispatch(fetchGRNs(buildParams()));
  }, [dispatch, debouncedSearch, locationId, statusFilter, postedFrom, postedTo, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "grnNumber",
        headerName: "GRN #",
        flex: 1.2,
        minWidth: 140,
        renderCell: ({ row }: GridValueGetterParams) => (
          <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => navigate(`/inventory/grn/${row._id}`)}>
            {row.grnNumber}
          </span>
        ),
      },
      { field: "location", headerName: "Location", flex: 1, minWidth: 120, valueGetter: (p: GridValueGetterParams) => p.row.locationId?.name || "" },
      { field: "items", headerName: "Items", flex: 0.6, minWidth: 80, valueGetter: (p: GridValueGetterParams) => (p.row.items || []).length },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 100,
        renderCell: ({ row }: GridValueGetterParams) => <Chip size="small" label={row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : ""} sx={{ color: STATUS_COLOR[row.status] || "#555" }} />,
      },
      { field: "postedAt", headerName: "Posted", flex: 1, minWidth: 140, valueGetter: (p: GridValueGetterParams) => (p.row.postedAt ? new Date(p.row.postedAt).toLocaleDateString() : "—") },
      // { field: "createdBy", headerName: "Created By", flex: 1.2, minWidth: 170, sortable: false, renderCell: ({ row }: GridValueGetterParams) => <CreatedByCell createdBy={row.createdBy} createdAt={row.createdAt} /> },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.6,
        minWidth: 80,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) => (
          <Tooltip title="Audit Log">
            <IconButton
              style={{ color: "#7c3aed", backgroundColor: "#f5f3ff", border: "0.1px solid #7c3aed", height: 35, width: 35 }}
              size="small" onClick={() => handleOpenAudit(row)}><AssignmentIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  const isEmpty = status !== ApiStatus.LOADING && (grns?.length || 0) === 0;

  return (
    <Box sx={{ width: "100%" }}>
      <FilterBar
        onReset={resetFilters}
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button startIcon={<AssessmentOutlinedIcon />} size="small" variant="outlined" onClick={() => navigate("/inventory/grn/report")}>
              View Report
            </Button>
            <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => navigate("/inventory/grn/new")}>
              New GRN
            </Button>
          </Box>
        }
      >
        <FilterSearch value={search} onChange={setSearch} placeholder="Search GRN #" width={200} />
        <LocationSelect borderless width={200} value={locationId} onChange={onLocation} label="Filter by location" activeOnly={false} />
        <FilterSelect
          value={statusFilter}
          onChange={onStatus}
          placeholder="Status"
          width={150}
          options={GRN_STATUSES.map((s) => ({ label: s.charAt(0).toUpperCase() + s.slice(1), value: s }))}
        />
        {/* The Filters dropdown already has its own rounded border; just notch the
            "Posted on" label onto that existing border (no extra box). */}
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Posted on</span>
          <Filters key={filtersKey} type="inventory-grn" dateOnly hidePartySearch hideFilter hideReset filterHandler={handlePostedDateFilter} />
        </div>
      </FilterBar>
      {isEmpty ? (
        hasFilters ? (
          <InventoryEmptyState
            icon={<ReceiptLongOutlinedIcon />}
            title="No Goods Receipts Found"
            description="No goods receipts match the current filters. Try adjusting your search, location, or status."
            caption="Need help? Check the Guide tab on inventory management"
          />
        ) : (
          <InventoryEmptyState
            icon={<ReceiptLongOutlinedIcon />}
            title="No Goods Receipts Yet"
            description="Record incoming stock by creating your first Goods Receipt Note. You can track received items, batches, and update inventory."
            actionLabel="Create Your First GRN"
            onAction={() => navigate("/inventory/grn/new")}
            caption="Need help? Check the Guide tab on inventory management"
          />
        )
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={grns}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(p: number) => setPage(p)}
            onPageSizeChange={onPageSizeChange}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}
      <Drawer
        anchor="right"
        open={auditDialogOpen}
        onClose={handleCloseAudit}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, maxWidth: "100%" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2, px: 3, backgroundColor: "#f5f3ff", borderBottom: "1px solid #e5e7eb" }}>
            <AssignmentIcon sx={{ color: "#7c3aed", fontSize: 26 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", fontSize: "1.1rem" }}>Audit Logs</Typography>
              <Typography variant="body2" sx={{ color: "#7c3aed", mt: 0.25, fontSize: "0.85rem" }}>
                {auditGRN?.grnNumber || ""}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseAudit}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", py: 2, px: 2 }}>
            {auditGRN && (
              <AuditTimeline entity="GRN" entityId={auditGRN._id} />
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default GRNList;
